import { useEffect, useRef, RefObject } from 'react';
import type { Editor } from '@tiptap/react';

interface UseKeepOnPageArgs {
  editor: Editor;
  /** NodeViewProps['getPos'] — returns the node's document position, or undefined if detached. */
  getPos: () => number | undefined;
}

// Subset of @tiptap-pro/extension-pages storage. Present only in "pages" view mode;
// returns px distance from a doc position to the next page boundary (null if none below).
interface PagesKeepOnPageStorage {
  getDistanceToNextPagebreak?: (pos: number) => number | null;
}

const readPagesStorage = (editor: Editor): PagesKeepOnPageStorage | undefined =>
  (editor.storage as { pages?: PagesKeepOnPageStorage }).pages;

/**
 * Keeps an atom NodeView together across page boundaries in Pages mode: when the node
 * would straddle a page break, applies `padding-top` (visual only — not document content)
 * to push it to the top of the next page. Reusable — spread the returned ref onto the
 * `<NodeViewWrapper>`. No-op outside Pages mode.
 */
export function useKeepOnPage({ editor, getPos }: UseKeepOnPageArgs): RefObject<HTMLDivElement> {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return undefined;

    const clearPadding = (): void => {
      el.style.removeProperty('padding-top');
    };

    // No-op outside Pages mode.
    const pages = readPagesStorage(editor);
    if (typeof pages?.getDistanceToNextPagebreak !== 'function') {
      clearPadding();
      return undefined;
    }

    let lastPad = 0;

    const recompute = (): void => {
      const pos = getPos();
      if (pos == null) return;

      // Sum children scrollHeight, not the wrapper rect: the inner element's max-height +
      // overflow clamps the rect to a value that flips across reflows and flickers the push.
      let height = 0;
      for (const child of Array.from(el.children)) height += (child as HTMLElement).scrollHeight;

      // Our padding shifts the node's screen position, which getDistanceToNextPagebreak
      // reads — so strip it to measure, then restore within the same JS task (no repaint).
      const restorePad = lastPad;
      el.style.removeProperty('padding-top');
      void el.offsetHeight;
      const distance = pages.getDistanceToNextPagebreak?.(pos) ?? null;
      if (restorePad) el.style.paddingTop = `${restorePad}px`;

      const pageMaxHeight =
        parseFloat(getComputedStyle(editor.view.dom).getPropertyValue('--page-max-height')) || Number.POSITIVE_INFINITY;

      // MARGIN gives hysteresis (no flip on the page edge) and the churn guard. Skip nodes
      // taller than the page — they can never fit, so let them flow.
      const MARGIN = 4;
      const fitsOnPage = distance != null && height <= pageMaxHeight;
      const overflows =
        distance != null && (lastPad > 0 ? height > distance - MARGIN : height > distance + MARGIN);
      const next = overflows && fitsOnPage ? (distance as number) : 0;

      if (Math.abs(next - lastPad) <= MARGIN) return;
      lastPad = next;
      if (next) {
        el.style.paddingTop = `${next}px`;
      } else {
        clearPadding();
      }
    };

    // Double rAF so we measure after the Pages extension's own re-pagination rAF settles.
    let raf = 0;
    const schedule = (): void => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        raf = requestAnimationFrame(recompute);
      });
    };

    const resizeObserver = new ResizeObserver(schedule);
    resizeObserver.observe(el);
    editor.on('update', schedule);

    schedule();

    return () => {
      cancelAnimationFrame(raf);
      resizeObserver.disconnect();
      editor.off('update', schedule);
      clearPadding();
    };
  }, [editor, getPos]);

  return wrapperRef;
}
